import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, TrendingUp, TrendingDown, Sparkles,
  Target, Wallet, Zap, Globe2, Layers3,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { PLATFORMS } from "@/data/site";
import { CheckerBg, ClimbingChart } from "@/components/EvaluationProcess.ClimbingChart";


/* ============================================================
   STEP 2 — Infinity loop (24/7 capital)
   ============================================================ */
function InfinityGraphic() {
  const { t } = useTranslation();
  return (
    <div className="relative aspect-[16/9] rounded-xl overflow-hidden grid place-items-center" style={{ background: "linear-gradient(180deg, rgba(170,222,235,0.06), rgba(0,0,0,0.4))", border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)" }}>
      <CheckerBg density={20} />
      {[
        { c: "rgba(170,222,235,", glow: "#AADEEB" },
        { c: "rgba(80,220,130,",  glow: "#54d97c" },
        { c: "rgba(240,90,90,",   glow: "#f06a6a" },
      ].map((ring, i) => (
        <motion.span key={`ring-${ring.glow}`} className="absolute rounded-full"
          style={{
            width: 80 + i * 40,
            height: 80 + i * 40,
            border: `1px solid ${ring.c}${0.32 - i * 0.07})`,
            boxShadow: `0 0 12px ${ring.c}0.18)`,
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.7, 0.3, 0.7] }}
          transition={{ duration: 3.4 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }} />
      ))}
      {/* Two counter-orbiting market ticks — one green ▲, one red ▼ */}
      {[
        { color: "#54d97c", glyph: "▲", r: 60, dur: 5.2, delay: 0 },
        { color: "#f06a6a", glyph: "▼", r: 88, dur: 7.0, delay: 0.8 },
      ].map((o, i) => (
        <motion.span
          key={`orbit-${o.glyph}`}
          className="absolute text-[11px] font-bold leading-none select-none"
          style={{ color: o.color, textShadow: `0 0 6px ${o.color}` }}
          animate={{
            x: [o.r, 0, -o.r, 0, o.r],
            y: [0, -o.r * 0.42, 0, o.r * 0.42, 0],
            opacity: [0.85, 1, 0.6, 1, 0.85],
          }}
          transition={{ duration: o.dur, repeat: Infinity, ease: "linear", delay: o.delay }}
        >
          {o.glyph}
        </motion.span>
      ))}
      <motion.div className="relative z-10" animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}>
        <svg viewBox="0 0 120 60" width="160" height="80">
          <defs>
            <linearGradient id="infGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5fb8cf" /><stop offset="50%" stopColor="#AADEEB" /><stop offset="100%" stopColor="#5fb8cf" />
            </linearGradient>
            <filter id="infGlow"><feGaussianBlur stdDeviation="2.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>
          <motion.path d="M 25 30 C 25 10, 50 10, 60 30 C 70 50, 95 50, 95 30 C 95 10, 70 10, 60 30 C 50 50, 25 50, 25 30 Z"
            fill="none" stroke="url(#infGrad)" strokeWidth="3" strokeLinecap="round" filter="url(#infGlow)"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.8, ease: "easeInOut" }} />
          <motion.circle r="3.5" fill="#fff">
            <animateMotion dur="3.2s" repeatCount="indefinite" path="M 25 30 C 25 10, 50 10, 60 30 C 70 50, 95 50, 95 30 C 95 10, 70 10, 60 30 C 50 50, 25 50, 25 30 Z" />
          </motion.circle>
        </svg>
      </motion.div>
      <motion.div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[12px] font-medium tracking-wider"
        style={{ background: "color-mix(in srgb, var(--brand) 18%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 50%, transparent)", color: "#AADEEB" }}
        animate={{ y: [0, -3, 0] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}>
        <Sparkles size={10} className="inline mr-1 -mt-0.5" /> {t("process.capital_247", "24 / 7 CAPITAL")}
      </motion.div>
      {/* Mini red/green tick rail at bottom — alive markets feel */}
      <div className="absolute bottom-2.5 right-3 flex items-center gap-1 z-10 font-mono text-[10px] uppercase tracking-[0.16em]">
        <motion.span className="inline-flex items-center gap-0.5" style={{ color: "#54d97c" }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.6, repeat: Infinity, delay: 0 }}>
          <TrendingUp size={9} strokeWidth={2.6} />+
        </motion.span>
        <motion.span className="inline-flex items-center gap-0.5" style={{ color: "#f06a6a" }}
          animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.6, repeat: Infinity, delay: 0.8 }}>
          <TrendingDown size={9} strokeWidth={2.6} />−
        </motion.span>
      </div>
    </div>
  );
}

/* ============================================================
   STEP 3 — SLOT MACHINE reel
   A vertical column of payout cards scrolls inside a fixed,
   bounded frame. Three rows are visible simultaneously: the
   previous payout peeks from above, the current sits centred
   in the highlight window, and the next peeks from below.
   Everything stays inside the parent box (overflow: hidden).
   ============================================================ */
const SLOT_ITEMS = [
  // direction alternates so the reel reads as a real market — wins
  // dominate (4 ups, 1 down) but the red row keeps it honest.
  { size: "$5K",   amount: 264,   cycle: "monthly", dir: "up",   delta: "+5.3%" },
  { size: "$25K",  amount: 1320,  cycle: "monthly", dir: "up",   delta: "+12.1%" },
  { size: "$50K",  amount: 2640,  cycle: "monthly", dir: "down", delta: "-2.4%" },
  { size: "$100K", amount: 5280,  cycle: "monthly", dir: "up",   delta: "+8.7%" },
  { size: "$250K", amount: 13200, cycle: "monthly", dir: "up",   delta: "+22.0%" },
];

function SlotRow({ item, dim }) {
  const { t } = useTranslation();
  const up = item.dir === "up";
  const Icon = up ? TrendingUp : TrendingDown;
  const colorFull = up ? "text-emerald-400" : "text-red-400";
  return (
    <div
      className="flex items-center justify-center"
      style={{ height: 72 }}
    >
      {/* The selection outline lives on each row and travels with it.
          Inactive rows render their content at 0 opacity AND have a
          transparent outline so the user never sees more than one
          framed row at a time — the active row IS the whole visual
          container. No nested boxes, no desync. */}
      <div
        className="relative flex flex-col items-center justify-center text-center select-none gap-1 rounded-md w-[92%] h-full px-3 transition-opacity duration-300"
        style={{
          border: dim
            ? "1.5px solid transparent"
            : "1.5px solid color-mix(in srgb, var(--brand) 70%, transparent)",
          boxShadow: dim
            ? "none"
            : "inset 0 0 0 1px color-mix(in srgb, var(--brand) 15%, transparent), 0 0 28px color-mix(in srgb, var(--brand) 18%, transparent)",
          opacity: dim ? 0 : 1,
        }}
      >
        {/* Bracket ticks on the active row only */}
        {!dim && (
          <>
            <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1.5 h-4 rounded-full bg-brand/70 shadow-[0_0_8px_rgba(170,222,235,0.6)]" />
            <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-1.5 h-4 rounded-full bg-brand/70 shadow-[0_0_8px_rgba(170,222,235,0.6)]" />
          </>
        )}
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] leading-tight">
          {t("process.funded_share", "{{size}} funded · 88% share", { size: item.size })}
        </div>
        <div className="text-2xl sm:text-3xl font-bold tabular-nums leading-none text-gradient-brand">
          ${item.amount.toLocaleString()}
        </div>
        <div className={`inline-flex items-center gap-1 text-[10px] leading-tight ${colorFull}`}>
          <Icon size={10} /> {item.delta} · {t("process.monthly_cycle", "monthly cycle")}
        </div>
      </div>
    </div>
  );
}

function RotatingPayoutCard() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLOT_ITEMS.length), 2200);
    return () => clearInterval(id);
  }, []);

  const ROW_H = 72;
  const total = SLOT_ITEMS.length;
  // Render a long flat list of N * COPIES rows. Translate by -idx * ROW_H.
  // COPIES is large enough that a long session never reaches the end.
  const COPIES = 80;
  const reel = useMemo(() => {
    const out = [];
    for (let c = 0; c < COPIES; c++) {
      for (let i = 0; i < total; i++) out.push({ ...SLOT_ITEMS[i], _key: `${c}-${i}`, _i: i });
    }
    return out;
  }, [total]);
  const yOffset = -idx * ROW_H;

  return (
    <div
      className="relative aspect-[16/9] rounded-xl overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #03070a 0%, #0a1418 50%, #03070a 100%)",
        border: "1px solid color-mix(in srgb, var(--brand) 22%, transparent)",
        boxShadow:
          "inset 0 2px 0 rgba(255,255,255,0.04), inset 0 -2px 0 rgba(0,0,0,0.6), inset 0 0 30px rgba(0,0,0,0.7)",
      }}
    >
      <CheckerBg density={20} />

      {/* "Live payouts" badge — pinned to top, reel viewport pushed
          below so the two never overlap on any viewport. */}
      <div className="absolute top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5 z-30"
           style={{ background: "color-mix(in srgb, var(--brand) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", color: "#AADEEB" }}>
        <ShieldCheck size={10} /> {t("process.live_payouts", "Live payouts")}
      </div>

      {/* Reel viewport — starts under the badge so it never overlaps.
          NO visible chrome of its own (no border, no inner shadow) —
          the active SlotRow's outline is the only frame the user
          sees, so it can never look nested inside another box. */}
      <div className="absolute inset-x-0 bottom-0 top-10 grid place-items-center">
        <div
          className="relative overflow-hidden"
          style={{
            width: "92%",
            maxWidth: 320,
            height: "82%",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 12%, #000 28%, #000 72%, rgba(0,0,0,0.4) 88%, transparent 100%)",
            maskImage:
              "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 12%, #000 28%, #000 72%, rgba(0,0,0,0.4) 88%, transparent 100%)",
          }}
        >
          {/* Translating column of rows. The motion.div is positioned at
              top:50% with a -50% translate so the FIRST row (idx=0) is
              centered when y=0. Subsequent rows scroll up. */}
          <motion.div
            className="absolute left-0 right-0"
            animate={{ y: yOffset - ROW_H / 2 }}
            transition={{ type: "spring", stiffness: 110, damping: 22, mass: 0.7 }}
            style={{ top: "50%" }}
          >
            {reel.map((it) => (
              <SlotRow key={it._key} item={it} dim={it._i !== idx % total ? true : false} />
            ))}
          </motion.div>
        </div>
      </div>

      {/* Selection frame now lives on each row (see SlotRow). Removing
          the old static overlay means the highlight can never desync
          from the row's actual position during the spring animation. */}

      {/* Top + bottom metallic bezels for slot-machine feel */}
      <div className="absolute top-0 inset-x-0 h-[18%] pointer-events-none"
           style={{ background: "linear-gradient(180deg, rgba(170,222,235,0.10) 0%, transparent 100%)" }} />
      <div className="absolute bottom-0 inset-x-0 h-[18%] pointer-events-none"
           style={{ background: "linear-gradient(0deg, rgba(170,222,235,0.10) 0%, transparent 100%)" }} />

      {/* Bottom dot pager */}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-30">
        {SLOT_ITEMS.map((item, i) => (
          <span key={`pager-${item.id || item.label || i}`} className="h-1 rounded-full transition-all" style={{
            width: i === idx ? 22 : 6,
            background: i === idx ? "#AADEEB" : "color-mix(in srgb, #AADEEB 30%, transparent)",
          }} />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Per-step bullets — vertical stack, one row per item.
   Each row: small icon-circle on the LEFT + label on the right.
   The 'platforms' bullet renders the 4 platform logos inline on
   its row so all 3 step cards have the SAME shape.
   ============================================================ */

function PlatformsBulletRow({ label }) {
  return (
    <div className="flex items-center gap-3" data-testid="bullet-row-platforms">
      <span
        className="w-7 h-7 rounded-full grid place-items-center shrink-0"
        style={{
          background: "color-mix(in srgb, var(--brand) 18%, transparent)",
          border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
        }}
      >
        <Layers3 size={13} className="text-brand" strokeWidth={2.4} />
      </span>
      <span className="text-[13px] text-[color:var(--text-1)] mr-1 whitespace-nowrap">{label}</span>
      <span className="flex items-center gap-1.5 flex-wrap">
        {PLATFORMS.map((p) => (
          <span
            key={p.id}
            title={p.name}
            className="w-7 h-7 rounded-full grid place-items-center shrink-0"
            style={{
              background: "color-mix(in srgb, var(--brand) 10%, transparent)",
              border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
            }}
          >
            <img src={p.logo} alt="" aria-hidden="true" className="w-5 h-5 object-contain" draggable={false} />
          </span>
        ))}
      </span>
    </div>
  );
}

function IconBulletRow({ it }) {
  const { t } = useTranslation();
  const label = it.labelKey ? t(it.labelKey, it.labelFb || it.label) : it.label;
  const sub = it.subKey ? t(it.subKey, it.subFb || it.sub) : it.sub;
  return (
    <div className="flex items-center gap-3" data-testid={`bullet-row-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span
        className="w-7 h-7 rounded-full grid place-items-center shrink-0"
        style={{
          background: "color-mix(in srgb, var(--brand) 18%, transparent)",
          border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
        }}
      >
        <it.icon size={13} className="text-brand" strokeWidth={2.4} />
      </span>
      <span className="text-[13px] text-[color:var(--text-1)] leading-tight">
        <span className="font-medium">{label}</span>
        {sub && (
          <span className="text-[color:var(--text-3)] font-light"> — {sub}</span>
        )}
      </span>
    </div>
  );
}

function StepBullets({ items }) {
  const { t } = useTranslation();
  return (
    <ul className="mt-5 flex flex-col gap-2.5" data-testid="step-bullets">
      {items.map((it, i) => {
        const resolvedLabel = it.labelKey ? t(it.labelKey, it.labelFb || it.label) : it.label;
        return (
          <li key={resolvedLabel || `pf-${i}`}>
            {it.type === "platforms" ? (
              <PlatformsBulletRow label={resolvedLabel || t("process.platforms_label", "Platforms")} />
            ) : (
              <IconBulletRow it={it} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ============================================================
   Step card shell
   ============================================================ */
function StepCard({ index, badge, title, subtitle, bullets, children }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.08 }}
      data-testid={`process-step-${index + 1}`}
      className="relative card-beam p-5 sm:p-6 lg:p-7 flex flex-col h-full"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand">{t("process.step_label", "Step")} {String(index + 1).padStart(2, "0")}</div>
          <h3 className="text-lg sm:text-xl font-medium text-[color:var(--text-1)] mt-1 leading-tight">{title}</h3>
        </div>
        <span className="px-2 sm:px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest text-brand border border-brand/30 bg-brand/[0.06] whitespace-nowrap shrink-0">
          {badge}
        </span>
      </div>

      <div>{children}</div>

      {bullets && <StepBullets items={bullets} />}

      <p className="text-sm text-[color:var(--text-2)] font-light mt-4 leading-relaxed">{subtitle}</p>
    </motion.div>
  );
}

/* ============================================================
   Animated connector — desktop horizontal, mobile vertical
   ============================================================ */
function Connector({ vertical = false }) {
  if (vertical) {
    return (
      <div className="lg:hidden relative h-10 flex items-center justify-center" aria-hidden>
        <svg width="20" height="40" viewBox="0 0 20 40" className="overflow-visible">
          <line x1="10" y1="0" x2="10" y2="32" stroke="#AADEEB" strokeOpacity="0.45" strokeWidth="1.5" strokeDasharray="4 4" />
          <motion.circle r="3" fill="#AADEEB">
            <animateMotion dur="1.4s" repeatCount="indefinite" path="M 10 0 L 10 32" />
          </motion.circle>
          <polygon points="10,40 5,32 15,32" fill="#AADEEB" />
        </svg>
      </div>
    );
  }
  return (
    <div className="hidden lg:flex relative items-center" aria-hidden style={{ minWidth: 48 }}>
      <svg width="100%" height="56" viewBox="0 0 100 56" preserveAspectRatio="none" className="overflow-visible">
        <line x1="0" y1="28" x2="86" y2="28" stroke="#AADEEB" strokeOpacity="0.45" strokeWidth="1.5" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        <motion.circle r="3.5" fill="#AADEEB" style={{ filter: "drop-shadow(0 0 4px #AADEEB)" }}>
          <animateMotion dur="1.6s" repeatCount="indefinite" path="M 0 28 L 86 28" />
        </motion.circle>
        <polygon points="100,28 84,20 84,36" fill="#AADEEB" />
      </svg>
    </div>
  );
}

/* ============================================================
   MAIN
   ============================================================ */
const STEP1_BULLETS = [
  { icon: Target,   labelKey: "process.s1_b1_label", labelFb: "Pick Your Challenge", subKey: "process.s1_b1_sub", subFb: "Instant · 1-step · 2-step" },
  { icon: Globe2,   labelKey: "process.s1_b2_label", labelFb: "150+ Instruments",    subKey: "process.s1_b2_sub", subFb: "FX · indices · gold · crypto" },
  { icon: Zap,      labelKey: "process.s1_b3_label", labelFb: "Live Simulated",      subKey: "process.s1_b3_sub", subFb: "Realistic spreads" },
];
const STEP2_BULLETS = [
  { icon: Zap,      labelKey: "process.s2_b1_label", labelFb: "24 – 48h Onboard",   subKey: "process.s2_b1_sub", subFb: "Pass KYC, start trading" },
  { type: "platforms", labelKey: "process.s2_b2_label", labelFb: "4 Platforms" },
  { icon: Sparkles, labelKey: "process.s2_b3_label", labelFb: "Always-On Capital",  subKey: "process.s2_b3_sub", subFb: "Markets open Mon – Fri 24/5" },
];
const STEP3_BULLETS = [
  { icon: Wallet,      labelKey: "process.s3_b1_label", labelFb: "Flexible Payouts", subKey: "process.s3_b1_sub", subFb: "Fiat or crypto" },
  { icon: ShieldCheck, labelKey: "process.s3_b2_label", labelFb: "Up to 90% Share",  subKey: "process.s3_b2_sub", subFb: "Account gains kept" },
  { icon: TrendingUp,  labelKey: "process.s3_b3_label", labelFb: "Monthly Cycle",    subKey: "process.s3_b3_sub", subFb: "Once per month" },
];

export default function EvaluationProcess() {
  const { t } = useTranslation();
  return (
    <section id="about" data-testid="evaluation-section" className="relative py-20 sm:py-14 lg:py-28 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12 sm:mb-14">
          <div className="pill-mono inline-flex mb-5">{t("process.pill", "How it works")}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-4">
            {t("process.title", "Get funded in")} <span className="text-gradient-brand font-semibold">{t("process.titleAccent", "3 simple steps")}</span>
          </h2>
          <p className="text-[color:var(--text-3)] max-w-2xl mx-auto text-sm lg:text-base font-light">
            {t("process.subtitle", "Prove your edge, claim your capital, get paid. No gimmicks, no hidden rules.")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_48px_1fr_48px_1fr] gap-5 lg:gap-0 items-stretch">
          <StepCard index={0} badge={t("process.s1_badge", "Trade")} title={t("process.s1_title", "Pass the Evaluation")}
            subtitle={t("process.s1_subtitle", "Hit your Equity Growth Target while respecting daily-loss and drawdown rules.")}
            bullets={STEP1_BULLETS}>
            <ClimbingChart />
          </StepCard>

          <Connector />
          <Connector vertical />

          <StepCard index={1} badge={t("process.s2_badge", "Activate")} title={t("process.s2_title", "Get Funded 24/7")}
            subtitle={t("process.s2_subtitle", "Onboard fast and trade on any of the four institutional platforms — capital never sleeps.")}
            bullets={STEP2_BULLETS}>
            <InfinityGraphic />
          </StepCard>

          <Connector />
          <Connector vertical />

          <StepCard index={2} badge={t("process.s3_badge", "Cash Out")} title={t("process.s3_title", "Claim Your Account Gains")}
            subtitle={t("process.s3_subtitle", "Monthly payouts in fiat or crypto. Keep up to 90% of simulated Account Gains.")}
            bullets={STEP3_BULLETS}>
            <RotatingPayoutCard />
          </StepCard>
        </div>

        <div className="mt-12 text-center">
          <a href="#challenges" data-testid="process-cta" className="btn-solid-brand text-sm inline-flex items-center">
            {t("process.cta", "Start your challenge")} <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
